import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { raygunHttpError } from '@approot/app.raygun.setup';
import {
  AgGridConfig,
  CutOffTimes,
  DepositTakerAndCurrencies,
  DvEntities,
  FixingSource,
  MedusaUser,
  ProductMenu,
  ProductOrder,
  UserSettings
} from '@approot/models';
import { Observable, ReplaySubject, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AlertsService } from '../alerts/alerts.service';
import { AuthService } from '../auth/auth.service';

type UserSettingsItem = keyof UserSettings;

export type TradeStatusItem = {
  longName: string;
  statuses: Record<string, boolean>;
};

export type TradeStatus = {
  statuses: string[];
  allowed_statuses: TradeStatusItem[];
};

export type TradeBookingParameters = UserSettings['tradeBookingParameters'];

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private userSettings$ = new ReplaySubject<UserSettings>(1);
  public userSettings = toSignal(this.userSettings$);
  private cache: UserSettings = null;

  constructor(
    private alertsService: AlertsService,
    private authService: AuthService
  ) {
    // pre-cache
    this.getUserInfo<UserSettings>({ task: 'getUserSettings' }).subscribe(u => {
      this.setSSOSettings(u.parent);
      // Need to set it in the auth service to avoid a circular dependency in
      // EntityMapperService when trying to import ConfigService
      this.authService.setEnforceEntityRedaction(
        !!u.parent.enforceEntityRedaction
      );

      // Redact for everyone until there's flag in user settings
      window.redactEntitiesInRaygun = true;

      this.cache = u;
      this.userSettings$.next(u);
    });
  }

  /**
   * Consider use userSettings signal instead. Note that code might need to be
   * modified to handle the initial `undefined` value.
   */
  public getUserSettings(): Observable<UserSettings> {
    if (this.cache) {
      return of(this.cache);
    }
    return this.userSettings$.asObservable();
  }

  public get getDepositTakersAndCurrencies(): Observable<
    DepositTakerAndCurrencies[]
  > {
    return this.getUserInfo<DepositTakerAndCurrencies[]>({
      task: 'getAvailableDepositTakersAndCurrencies'
    });
  }

  public getMedusaCredentials(): Observable<MedusaUser[]> {
    return this.getUserInfo<MedusaUser[]>({
      task: 'getMedusaCredentials'
    });
  }

  public getTradeBookingParameters(): Observable<TradeBookingParameters> {
    return this.getUserSettingsItem<TradeBookingParameters>(
      'tradeBookingParameters'
    );
  }

  public getAgGridConfig(): Observable<AgGridConfig[]> {
    return this.getUserSettingsItem<AgGridConfig[]>('ag_grid_config');
  }

  public getOrderingObject(): Observable<ProductOrder[]> {
    return this.getUserSettingsItem<ProductOrder[]>('order');
  }

  public getFixingSources(): Observable<FixingSource[]> {
    return this.getUserSettingsItem<FixingSource[]>('fixingsource');
  }

  public getProductTypeMenu(): Observable<ProductMenu[]> {
    return this.getUserSettingsItem<ProductMenu[]>('menu');
  }

  public getCutOffTimes(): Observable<CutOffTimes[]> {
    return this.getUserSettingsItem<CutOffTimes[]>('cutofftime');
  }

  public getAllowedTradeStatuses(): Observable<TradeStatus> {
    return this.getUserInfo<TradeStatus>({
      task: 'getAllowedTradeStatuses'
    });
  }

  /******************** PRIVATE ***********************/

  private setSSOSettings(u: DvEntities) {
    if (u?.ssoPortalAction) {
      sessionStorage.setItem('ssoPortalAction', u.ssoPortalAction);
    } else {
      sessionStorage.removeItem('ssoPortalAction');
    }
    if (u?.ssoPortalURL) {
      sessionStorage.setItem('ssoPortalURL', u.ssoPortalURL);
    } else {
      sessionStorage.removeItem('ssoPortalURL');
    }
  }

  private getUserSettingsItem<T>(item: UserSettingsItem): Observable<T> {
    return this.userSettings$
      .asObservable()
      .pipe(switchMap(userSettings => of<T>(userSettings[item as any])));
  }

  private handleError(error: HttpErrorResponse) {
    this.alertsService.addHttpAlert({ error });
    return throwError(
      () => new Error('Something bad happened; please try again later.')
    );
  }

  private getUserInfo<T>(params: Record<string, string>): Observable<T> {
    const httpParams = new HttpParams().appendAll(params).toString();
    return this.authService.fxPost<T>(params, `get-user-info`, httpParams).pipe(
      catchError(err => {
        raygunHttpError(err, ['get-user-info']);
        return this.handleError(err);
      })
    );
  }
}
